/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable @typescript-eslint/typedef */
import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';

import { CmsCore, CmsPlanSwitch } from 'store/cms/models';
import { AccountSummary, LineSummary } from 'store/account-summary/models';
import { Accessibility, CimaCore, Cloudinary, Util, XmStore, XmStoreUtil } from 'core/services';
import { PlanOption } from 'my-account/interface';
import { DiscountPromoCollection, DiscountPromotion } from 'store/promotions/discount-promotion/models';
import { StoreAction } from 'store/actions';
import { CONFIG_TOKEN } from 'core/constants';
import { IXMOptions } from 'core/interfaces';
import { targetPlanSwitchDetails } from 'store/plan-eligibility/models/target-plan-switch';
import { User } from 'store/user/models/user';

@Component({
    selector: 'xm-bucket-switch',
    templateUrl: 'bucket-switch.html',
    styleUrls: ['bucket-switch.scss'],
    standalone: false
})
export class XmBucketSwitch implements OnInit, OnDestroy {
    @Input() public pageData: CmsBucketSwitchCarousal;
    @Input() public availablePlanOptions: PlanOption[];
    @Input() public accountSummary: AccountSummary;
    @Input() public lineSummary: LineSummary;
    @Input() public showIcopPromotions?: boolean;
    @Input() public user: User;

    @Output() public selectedPlanObject: EventEmitter<PlanOption> = new EventEmitter<PlanOption>();

    public currentSlideIndex: number;
    public checkmarkIcon: MediaImageOptions;
    public carouselSettings: XmSwiperOptions;
    public overrideButtonsAriaLabel: CmsCarousel;
    public optionStepDescription: string;
    public selectedPlan: PlanOption;
    public promoResponse: DiscountPromoCollection;
    public isDiscountPromotion: boolean = false;
    public promotionType: string;
    public promoContent: string;
    public promoBYOContent: string;
    public promoUNLContent: string;
    public tradeInDDAPromoContent: string;
    public extendedPromoContent: string;
    public hasSwitchPromoBYO: boolean = false;
    public showBogoPromoLossForUnlLines: boolean = false;
    public isIcopPlanSwitch: boolean = false;
    public legacyPromoDetails: targetPlanSwitchDetails;
    public showSegmentUpgradeBanner: boolean;
    public segmentSwitchBanner: NotificationBanner;
    public segmentSwitchIcon: MediaImageOptions;
    public isBusinessUser: boolean;

    private accessibility: Accessibility;
    private xmStore: XmStore;
    private subscriptions: Subscription[] = [];
    private cimaCore: CimaCore;
    private config: IXMOptions;

    constructor(xmStore: XmStore, accessibility: Accessibility, cimaCore: CimaCore, @Inject(CONFIG_TOKEN) config: IXMOptions) {
        Object.assign(this, { xmStore, accessibility, cimaCore, config });
    }

    public ngOnInit(): void {
        this.isBusinessUser = this.cimaCore.isBusinessUser;
        this.isIcopPlanSwitch = this.config.ENABLE_ICOP_P1_PLANSWITCH;
        this.showBogoPromoLossForUnlLines = this.lineSummary?.numberOfLinesInThePlan < 3;
        this.showSegmentUpgradeBanner = this.user.account.canMoveLevel || ((this.user.account.isSegmentFive && !this.isBusinessUser)
            && !this.user.account.isNYCustomer);
        this.availablePlanOptions.map((plan: PlanOption, index: number) => {
            if (plan.isSelected) {
                this.selectedPlan = plan;
                this.currentSlideIndex = index;
            }
        });

        this.optionStepDescription = this.getOptionStepDescription(this.selectedPlan);

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsCore>(CmsCore), (result: CmsCore) => {
            this.checkmarkIcon = {
                mobile: Cloudinary.generateMediaFromCms(result.icons.greenCircleCheck)
            };
        }));

        this.overrideButtonsAriaLabel = this.pageData.overrideButtonsAriaLabel;
        this.carouselSettings = this.pageData.carousalSettings;
        if (!this.isIcopPlanSwitch) {
            XmStoreUtil.defaultCatch(this.xmStore.findPromise<DiscountPromoCollection>(StoreAction.GET_DISCOUNT_PROMOTION, DiscountPromoCollection))
                .then((promoResponse: DiscountPromoCollection) => {
                    this.promoResponse = promoResponse;
                    this.planSwitchPromoEligible();
                });
        }
        if (this.showSegmentUpgradeBanner) {
            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<targetPlanSwitchDetails>(targetPlanSwitchDetails), (legacyPromoDetails: targetPlanSwitchDetails) => {
                this.legacyPromoDetails = legacyPromoDetails;
                this.segmentSwitchBanner = this.legacyPromoDetails.selectionPage?.banners.length ? this.legacyPromoDetails.selectionPage.banners[0] : undefined;
                this.segmentSwitchIcon = {
                    mobile: {
                        base: this.segmentSwitchBanner?.image
                    }
                };
            }));
        }
    }

    /** 
     * This method is designed to dynamically update promotional content and eligibility based on the current plan switch context. 
     * It handles multiple promotion types and ensures that the UI can display the correct promotional information to the user based on their eligibility.
    */
    public planSwitchPromoEligible(): void {
        this.hasSwitchPromoBYO = this.promoResponse?.isLinePromoBYOEligible ?? false; // performing null check if the promoResponse is not available
        this.isDiscountPromotion = this.promoResponse && (this.promoResponse.isLinePromoEligible || this.promoResponse.isAccountPromoEligible || this.promoResponse.isTradeInDDAPromoEligible || this.promoResponse.isExtendedPromoDollarEligible);
        
        if (this.isDiscountPromotion) {
            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsPlanSwitch>(CmsPlanSwitch), (resp: CmsPlanSwitch) => {
                const promo: CmsPlanSwitchMultiplePromotions = resp.planSwitchMultiplePromotions;
                this.promoContent = promo.reviewDescription.general;
                this.promoBYOContent = this.promoResponse.isLinePromoBYOEligible ? promo.reviewDescription.BYO : undefined;
                if (this.promoResponse.isLinePromoUNLFREEEligible || this.promoResponse.isLinePromoUNLHL25Eligible) {
                    const servicePromoLine = this.promoResponse.mvnoPromotions.find(promo => promo.type === 'SERVICE_PROMO_LINE' && (promo.lineId === this.lineSummary.id.key || promo.lineId === this.lineSummary.id.value));
                    if (servicePromoLine) {
                        this.promoUNLContent = this.cimaCore.isBusinessUser ? promo.reviewCBMDescription.UNLFREE : promo.reviewDescription.UNLFREE;
                        this.promoUNLContent = this.promoUNLContent.replace('[PROMOTION_TERM]', servicePromoLine.promotionTerm.toString());
                    }
                } else if (this.promoResponse.isAccountPromoEligible) {
                    this.promoUNLContent = promo.reviewDescription.UNL;
                }
                const tradeInDDAPromo: DiscountPromotion = this.promoResponse.tradeInDDAPromoEligible.find((data: DiscountPromotion) => data.lineId === this.lineSummary.id.key || data.lineId === this.lineSummary.id.value);
                const extendedDDAPromo: DiscountPromotion = this.promoResponse.extendedPromoDollarEligible.find((data: DiscountPromotion) => data.lineId === this.lineSummary.id.key || data.lineId === this.lineSummary.id.value);

                if (tradeInDDAPromo) {
                    if (this.cimaCore.isBusinessUser) {
                        this.promoContent = promo.reviewCBMDescription.general;
                        this.tradeInDDAPromoContent = promo.reviewCBMDescription.DDA.replace('[PROMO_AMOUNT]', tradeInDDAPromo.billerInfo.promotionAmount.toString());
                    } else {
                        this.tradeInDDAPromoContent = promo.reviewDescription.DDA.replace('[PROMO_AMOUNT]', tradeInDDAPromo.billerInfo.promotionAmount.toString());
                    }
                }
                if (extendedDDAPromo) {
                    if (this.cimaCore.isBusinessUser) {
                        this.promoContent = promo.reviewCBMDescription.general;
                        this.extendedPromoContent = promo.reviewCBMDescription.EPD.replace('[PROMO_AMOUNT]', extendedDDAPromo.billerInfo.promotionAmount.toString());
                    } else {
                        this.extendedPromoContent = promo.reviewDescription.EPD.replace('[PROMO_AMOUNT]', extendedDDAPromo.billerInfo.promotionAmount.toString());
                    }
                }                
            }));
        }
    }

    public changePlan(index: number): void {
        this.changePlanOption(this.availablePlanOptions[index]);
    }

    public changePlanOption(plan: PlanOption): void {
        this.availablePlanOptions.forEach((planOption: PlanOption) => {
            planOption.isSelected = false;
        });

        this.selectedPlan = plan;
        plan.isSelected = true;

        const isMaxedOut: boolean = (this.availablePlanOptions.indexOf(this.selectedPlan) === (this.availablePlanOptions.length - 1)) && !plan.planData.isEligible && !plan.planData.requiresUpgrade;

        this.optionStepDescription = this.getOptionStepDescription(this.selectedPlan, isMaxedOut);

        this.accessibility.announceReaderText(this.pageData.planSelectionReader.replace('[BUCKET_SIZE]', this.selectedPlan && this.getDataSize(this.selectedPlan)));
    }

    public onCancel(): void {
        this.selectedPlanObject.emit();
    }

    public navToReview(): void {
        this.selectedPlanObject.emit(this.selectedPlan);
    }

    public ngOnDestroy(): void {
        Util.unsubscribeAll(this.subscriptions);
    }

    public getDataSize(plan: PlanOption): string {
        return plan.cmsPlanData.bucketSize || plan.planData.sharedDataGB;
    }

    public getDataPrice(plan: PlanOption): string {
        return plan.cmsPlanData.pricePerUnit || (plan.planData.postSwitch && plan.planData.postSwitch.planGroupCost.toString());
    }

    private getOptionStepDescription(plan: PlanOption, isMaxedOut: boolean = false): string {
        if (plan.planData.isIneligibleChangePending) {
            return this.replaceContent(this.pageData.description.ineligibleChangePending, plan);
        }

        if (plan.planData.isIneligibleBucket) {
            return this.replaceContent(this.pageData.description.ineligibleBucket, plan);
        }

        if (isMaxedOut) {
            return this.accountSummary.hasSharedPlan ? this.replaceContent(this.pageData.description.ineligibleWithSharedPlan, plan) :
                this.replaceContent(this.pageData.description.ineligibleOverageWithOutSharedPlan, plan);
        }

        if (plan.planData.isEligible && this.accountSummary.hasSharedPlan) {
            return this.replaceContent(this.pageData.description.eligibleWithSharedPlan, plan);
        }

        if (plan.planData.isEligible && !this.accountSummary.hasSharedPlan) {
            return this.replaceContent(this.pageData.description.eligibleWithoutSharedPlan, plan);
        }

        if (plan.planData.requiresUpgrade && this.accountSummary.hasSharedPlan) {
            return this.replaceContent(this.pageData.description.requiresUpgradeWithSharedPlan, plan);
        }

        if (plan.planData.requiresUpgrade && !this.accountSummary.hasSharedPlan) {
            return this.replaceContent(this.pageData.description.requiresUpgradeWithoutSharedPlan, plan);
        }

        if (plan.planData.isIneligibleInUse) {
            return this.replaceContent(this.pageData.description.ineligibleInUse, plan);
        }

        if (plan.planData.isIneligibleOverage && this.accountSummary.hasSharedPlan && !isMaxedOut) {
            return this.replaceContent(this.pageData.description.ineligibleOverageWithSharedPlan, plan);
        }

        if (plan.planData.isIneligibleOverage && !this.accountSummary.hasSharedPlan && !isMaxedOut) {
            return this.replaceContent(this.pageData.description.ineligibleOverageWithOutSharedPlan, plan);
        }

        if (this.accountSummary.hasSharedPlan) {
            return this.replaceContent(this.pageData.description.ineligibleWithoutSharedPlan, plan);
        }

        return this.replaceContent(this.pageData.description.ineligibleWithSharedPlan, plan);
    }

    private replaceContent(str: string, plan: PlanOption): string {
        return str.replace(/\[ACCOUNT_DATA_USAGE\]/g, this.accountSummary && this.accountSummary.sharedPlan && Util.mbToGb(this.accountSummary.sharedPlan.dataUsage))
            .replace(/\[NICKNAME\]/g, this.lineSummary && this.lineSummary.displayName)
            .replace(/\[DATA_USAGE\]/g, this.lineSummary && Util.mbToGb(this.lineSummary.dataUsage))
            .replace(/\[BUCKET_SIZE\]/g, plan && this.getDataSize(plan));
    }
}
